import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api'; // Standard dynamic import path relative to your project layout
import { 
  FiHardDrive, 
  FiTrash2, 
  FiDownload, 
  FiFile, 
  FiFolder, 
  FiRefreshCw,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiSearch,
  FiGrid,
  FiList,
  FiEye,
  FiFileText,
  FiImage,
  FiArchive,
  FiClock,
  FiChevronRight,
  FiHome,
  FiRefreshCcw
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import TenantAdminLayout from '../Layout/TenantAdminLayout';

// Fallback constant for UI endpoints if required by previews
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(3)) + ' ' + sizes[i];
};

// Get file icon based on type
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  const docExts = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'];
  const excelExts = ['xls', 'xlsx', 'csv', 'ods'];
  const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'];
  const audioExts = ['mp3', 'wav', 'ogg', 'flac', 'aac'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'];
  
  if (imageExts.includes(ext)) return FiImage;
  if (docExts.includes(ext)) return FiFileText;
  if (excelExts.includes(ext)) return FiFileText;
  if (videoExts.includes(ext)) return FiFile;
  if (audioExts.includes(ext)) return FiFile;
  if (archiveExts.includes(ext)) return FiArchive;
  return FiFile;
};

// Get color based on file type
const getFileColor = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const docExts = ['pdf', 'doc', 'docx', 'txt'];
  const excelExts = ['xls', 'xlsx', 'csv'];
  
  if (imageExts.includes(ext)) return 'text-violet-500 bg-violet-50 border-violet-100';
  if (docExts.includes(ext)) return 'text-rose-500 bg-rose-50 border-rose-100';
  if (excelExts.includes(ext)) return 'text-emerald-500 bg-emerald-50 border-emerald-100';
  return 'text-slate-500 bg-slate-50 border-slate-100';
};

// Check if file is an image
const isImageFile = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext);
};

// Check if file is a PDF
const isPDFFile = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return ext === 'pdf';
};

const StorageManagement = () => {
  const location = useLocation();  
  const tenantId = location.state?.tenantId || localStorage.getItem('tenantId');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [storageInfo, setStorageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedFilePreview, setSelectedFilePreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  
  // Refs for request throttling
  const lastSyncTime = useRef(0);
  const isSyncingRef = useRef(false);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Fetch files and folders for current path
  const fetchFiles = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Fetch storage info via custom api interceptor instance
      const storageRes = await api.get(`api/files/storage-info/${tenantId}`);
      setStorageInfo(storageRes.data.data);

      // Fetch files and folders using browse endpoint
      const queryPath = currentPath ? `?path=${encodeURIComponent(currentPath)}` : '';
      const filesRes = await api.get(`api/files/browse/${tenantId}${queryPath}`);

      if (filesRes.data.success) {
        setFolders(filesRes.data.data.folders || []);
        setFiles(filesRes.data.data.files || []);
        
        // Update breadcrumbs
        if (currentPath) {
          const parts = currentPath.split('/').filter(Boolean);
          setBreadcrumbs(parts);
        } else {
          setBreadcrumbs([]);
        }
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      showNotification('Failed to fetch files', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentPath, showNotification]);

  // Sync storage usage - with throttling
  const syncStorageUsage = useCallback(async () => {
    // Prevent multiple simultaneous sync requests
    if (isSyncingRef.current) {
      showNotification('Sync already in progress...', 'info');
      return;
    }

    // Throttle: minimum 10 seconds between syncs
    const now = Date.now();
    if (now - lastSyncTime.current < 10000) {
      const remaining = Math.ceil((10000 - (now - lastSyncTime.current)) / 1000);
      showNotification(`Please wait ${remaining}s before syncing again`, 'info');
      return;
    }

    if (!tenantId) {
      showNotification('Tenant ID missing', 'error');
      return;
    }

    // Set syncing state
    isSyncingRef.current = true;
    setSyncing(true);
    lastSyncTime.current = now;

    try {
      const response = await api.post(`api/files/sync-storage/${tenantId}`, {});

      if (response.data.success) {
        showNotification(
          `Storage synced! ${response.data.data.totalFiles} files, ${parseFloat(response.data.data.totalSizeGB).toFixed(3)} GB used`,
          'success'
        );
        
        // Refresh storage info after sync
        const storageRes = await api.get(`api/files/storage-info/${tenantId}`);
        setStorageInfo(storageRes.data.data);
      } else {
        showNotification('Sync failed: ' + (response.data.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Sync error:', error);
      showNotification(
        error.response?.data?.message || 'Failed to sync storage usage',
        'error'
      );
    } finally {
      isSyncingRef.current = false;
      setSyncing(false);
    }
  }, [tenantId, showNotification]);

  // Load data on mount and when tenantId/currentPath changes
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Navigate to folder
  const navigateToFolder = (folderPath) => {
    const relativePath = folderPath.replace(`tenants/${tenantId}/`, '').replace(/\/$/, '');
    setCurrentPath(relativePath);
  };

  // Navigate up
  const navigateUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  // Go to root
  const goToRoot = () => {
    setCurrentPath('');
  };

  // Handle single file delete
  const handleDeleteFile = async (fileKey) => {
    setDeleting(true);
    try {
      const response = await api.delete(`api/files/universal/${encodeURIComponent(fileKey)}`);

      if (response.data.success) {
        showNotification('File deleted successfully!', 'success');
        setFileToDelete(null);
        setShowDeleteModal(false);
        fetchFiles();
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Failed to delete file', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;

    setDeleting(true);
    try {
      const deletePromises = selectedFiles.map(fileKey =>
        api.delete(`api/files/universal/${encodeURIComponent(fileKey)}`)
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (successful > 0) {
        showNotification(`${successful} file(s) deleted successfully${failed > 0 ? `, ${failed} failed` : ''}`, 'success');
      } else {
        showNotification('Failed to delete files', 'error');
      }

      setSelectedFiles([]);
      fetchFiles();
    } catch (error) {
      console.error('Bulk delete error:', error);
      showNotification('Bulk delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Delete all files in current folder
  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const deletePromises = files.map(file =>
        api.delete(`api/files/universal/${encodeURIComponent(file.key)}`)
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (successful > 0) {
        showNotification(`${successful} file(s) deleted successfully${failed > 0 ? `, ${failed} failed` : ''}`, 'success');
      } else {
        showNotification('Failed to delete files', 'error');
      }

      setSelectedFiles([]);
      setShowDeleteAllModal(false);
      fetchFiles();
    } catch (error) {
      console.error('Delete all error:', error);
      showNotification('Failed to delete files', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Toggle file selection
  const toggleFileSelection = (fileKey) => {
    setSelectedFiles(prev =>
      prev.includes(fileKey)
        ? prev.filter(key => key !== fileKey)
        : [...prev, fileKey]
    );
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredFiles.map(f => f.key));
    }
  };

  // Handle file preview
  const handleFilePreview = (file) => {
    setSelectedFilePreview(file);
    setShowPreviewModal(true);
  };

  // Render file preview content
  const renderPreviewContent = () => {
    if (!selectedFilePreview) return null;
    
    const fileUrl = `${API_BASE}${selectedFilePreview.downloadUrl}`;
    const fileName = selectedFilePreview.filename;
    const isImage = isImageFile(fileName);
    const isPDF = isPDFFile(fileName);
    
    if (isImage) {
      return (
        <div className="flex items-center justify-center h-full bg-slate-50 rounded-xl p-4 border border-slate-100">
          <img 
            src={fileUrl} 
            alt={fileName} 
            className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-sm"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '';
              e.target.alt = 'Failed to load image';
            }}
          />
        </div>
      );
    }
    
    if (isPDF) {
      return (
        <div className="h-full min-h-[550px] bg-slate-50 p-2 rounded-xl border border-slate-100">
          <iframe
            src={`${fileUrl}#toolbar=1`}
            className="w-full h-full min-h-[550px] rounded-lg shadow-inner border-0"
            title={fileName}
          />
        </div>
      );
    }
    
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
        <div className="p-4 bg-white rounded-2xl shadow-sm mb-4 border border-slate-100">
          <FiFile className="w-16 h-16 text-slate-400" />
        </div>
        <p className="text-slate-700 font-medium text-center">
          Preview isn't available for this file type
        </p>
        <p className="text-slate-400 text-sm mt-1 max-w-xs text-center truncate">{fileName}</p>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 shadow-sm shadow-indigo-100 transition-all active:scale-[0.98]"
        >
          <FiDownload className="w-4 h-4" />
          Download File
        </a>
      </div>
    );
  };

  // Search filter for current files
  const filteredFiles = useMemo(() => {
    if (!searchTerm) return files;
    return files.filter(file =>
      file.filename.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [files, searchTerm]);

  const filteredFolders = useMemo(() => {
    if (!searchTerm) return folders;
    return folders.filter(folder =>
      folder.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [folders, searchTerm]);

  // Get total size of files in current folder
  const totalSize = useMemo(() => {
    return files.reduce((sum, file) => sum + file.size, 0);
  }, [files]);

  // Get display path
  const displayPath = useMemo(() => {
    if (!currentPath) return 'Root Directory';
    return currentPath;
  }, [currentPath]);

  return (
    <TenantAdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-slate-800 antialiased">
        {/* Notification */}
        <AnimatePresence>
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className={`fixed top-6 right-6 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-md border backdrop-blur-md ${
                notification.type === 'success' ? 'bg-emerald-50/90 border-emerald-200 text-emerald-900 shadow-emerald-100/40' :
                notification.type === 'error' ? 'bg-rose-50/90 border-rose-200 text-rose-900 shadow-rose-100/40' :
                notification.type === 'info' ? 'bg-indigo-50/90 border-indigo-200 text-indigo-900 shadow-indigo-100/40' :
                'bg-slate-50/90 border-slate-200 text-slate-900'
              }`}
            >
              {notification.type === 'success' ? <FiCheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" /> :
               notification.type === 'error' ? <FiAlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0" /> :
               <FiInfo className="w-5 h-5 text-indigo-600 flex-shrink-0" />}
              <span className="flex-1 text-sm font-medium">{notification.message}</span>
              <button onClick={() => setNotification(null)} className="flex-shrink-0 p-1 hover:bg-black/5 rounded-lg transition-colors">
                <FiX className="w-4 h-4 opacity-70" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top Hub Header */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Storage Control Center</h1>
            <p className="text-slate-500 text-sm mt-1">Browse, inspect, clean up, and handle system files for your workspace.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={fetchFiles}
              disabled={loading}
              className="p-2.5 text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-slate-100 rounded-xl transition-all disabled:opacity-50 active:scale-95"
              title="Refresh Workspace"
            >
              <FiRefreshCw className={`w-5 h-5 ${loading ? 'animate-spin text-indigo-600' : ''}`} />
            </button>
            
            <button
              onClick={syncStorageUsage}
              disabled={syncing || loading}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all active:scale-[0.98] ${
                syncing 
                  ? 'bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed'
                  : 'bg-indigo-50/60 text-indigo-700 border-indigo-100 hover:bg-indigo-50 shadow-sm'
              }`}
              title="Sync with Cloudflare R2 Cloud storage"
            >
              <FiRefreshCcw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing System...' : 'Sync Cloud Vault'}
            </button>

            {selectedFiles.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-rose-100 active:scale-[0.98] disabled:opacity-50"
              >
                <FiTrash2 className="w-4 h-4" />
                Discard Selected ({selectedFiles.length})
              </button>
            )}
            
            {files.length > 0 && (
              <button
                onClick={() => setShowDeleteAllModal(true)}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-100 text-sm font-medium rounded-xl transition-all active:scale-[0.98] disabled:opacity-50"
              >
                <FiTrash2 className="w-4 h-4" />
                Empty Current Folder
              </button>
            )}
          </div>
        </div>

        {/* Storage Analytics Grids */}
        {storageInfo && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100/50">
                <FiHardDrive className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Used Space</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">
                  {parseFloat(storageInfo.currentUsageGB).toFixed(3)} GB
                </p>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100/50">
                <FiCheckCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Available</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">
                  {parseFloat(storageInfo.availableGB).toFixed(3)} GB
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl border border-blue-100/50">
                <FiFile className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">File Count</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{files.length} items</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
              <div className="p-3 bg-purple-50 text-purple-600 rounded-xl border border-purple-100/50">
                <FiClock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Current Plan</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5 truncate max-w-[130px]" title={storageInfo.planName}>
                  {storageInfo.planName}
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl border border-amber-100/50">
                <FiRefreshCcw className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Last Synced</p>
                <p className="text-sm font-bold text-slate-900 mt-1">
                  {storageInfo.lastUpdated ? new Date(storageInfo.lastUpdated).toLocaleTimeString() : 'Never'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress System Bar */}
        {storageInfo && (
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
            <div className="flex justify-between items-center text-sm mb-2">
              <span className="font-semibold text-slate-700">Storage Track Allocation</span>
              <span className="text-slate-500 font-mono text-xs bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-md">
                {parseFloat(storageInfo.currentUsageGB).toFixed(3)} GB / {parseFloat(storageInfo.limitGB).toFixed(3)} GB ({parseFloat(storageInfo.usagePercentage).toFixed(3)}%)
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden p-0.5 border border-slate-200/50">
              <div
                className={`h-2 rounded-full transition-all duration-700 shadow-sm ${
                  storageInfo.usagePercentage > 80 ? 'bg-gradient-to-r from-rose-500 to-red-600' :
                  storageInfo.usagePercentage > 60 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                  'bg-gradient-to-r from-indigo-500 to-violet-600'
                }`}
                style={{ width: `${Math.min(storageInfo.usagePercentage, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Smart Trail Navigator / Path Track */}
        <div className="flex flex-wrap items-center gap-2 mb-6 bg-slate-50/80 border border-slate-200/60 p-2.5 rounded-xl shadow-inner text-sm">
          <button
            onClick={goToRoot}
            className="flex items-center gap-1.5 px-2.5 py-1 text-slate-600 hover:text-indigo-600 hover:bg-white border border-transparent hover:border-slate-100 rounded-lg transition-all font-medium"
          >
            <FiHome className="w-4 h-4" />
            <span>Root</span>
          </button>
          
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <FiChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <button
                onClick={() => {
                  const path = breadcrumbs.slice(0, index + 1).join('/');
                  setCurrentPath(path);
                }}
                className={`px-2.5 py-1 rounded-lg border border-transparent transition-all ${
                  index === breadcrumbs.length - 1 
                    ? 'font-semibold text-indigo-600 bg-white border-slate-200/80 shadow-sm' 
                    : 'text-slate-600 hover:text-indigo-600 hover:bg-white hover:border-slate-100'
                }`}
              >
                {crumb}
              </button>
            </React.Fragment>
          ))}
          
          {currentPath && (
            <button
              onClick={navigateUp}
              className="ml-auto px-3 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-800 text-xs font-medium rounded-lg shadow-sm transition-all flex items-center gap-1"
            >
              <span className="text-sm font-semibold leading-none">↑</span> Back Up
            </button>
          )}
        </div>

        {/* Directory Metadata Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 px-1">
          <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md text-xs">{displayPath}</span>
            {(files.length > 0 || folders.length > 0) && <span className="text-slate-300">•</span>}
            {files.length > 0 && (
              <span>
                {files.length} file{files.length !== 1 ? 's' : ''} 
                <span className="text-slate-400 font-mono ml-0.5">({formatFileSize(totalSize)})</span>
              </span>
            )}
            {folders.length > 0 && (
              <>
                {files.length > 0 && <span className="text-slate-300">•</span>}
                <span>{folders.length} folder{folders.length !== 1 ? 's' : ''}</span>
              </>
            )}
          </div>
          {files.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="text-xs font-semibold tracking-wide uppercase text-indigo-600 hover:text-indigo-800 bg-indigo-50/40 hover:bg-indigo-50 border border-indigo-100/50 px-2.5 py-1 rounded-lg transition-colors"
            >
              {selectedFiles.length === files.length ? 'Deselect Everything' : 'Select Everything Here'}
            </button>
          )}
        </div>

        {/* Search Engine & Layout Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 shadow-sm rounded-xl">
            <FiSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search files inside this workspace directory..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200/40 self-end sm:self-auto shadow-inner">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'grid' 
                  ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/20' 
                  : 'text-slate-400 hover:text-slate-700'
              }`}
            >
              <FiGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'list' 
                  ? 'bg-white text-indigo-600 shadow-sm border border-slate-200/20' 
                  : 'text-slate-400 hover:text-slate-700'
              }`}
            >
              <FiList className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Interactive Workspace Area */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="animate-spin rounded-full h-9 w-9 border-2 border-indigo-600 border-t-transparent"></div>
            <p className="text-slate-400 text-xs mt-3 font-medium">Reading cluster data...</p>
          </div>
        ) : (
          <>
            {/* Folder Grid Layout */}
            {filteredFolders.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2 px-1">
                  <FiFolder className="w-3.5 h-3.5" />
                  Folders ({filteredFolders.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {filteredFolders.map((folder) => (
                    <motion.div
                      key={folder.path}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white p-4 rounded-2xl border border-slate-200/80 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group relative"
                      onClick={() => navigateToFolder(folder.path)}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="p-3 bg-amber-50/60 rounded-2xl border border-amber-100/50 mb-3 group-hover:scale-105 group-hover:bg-amber-50 transition-all duration-300">
                          <FiFolder className="w-10 h-10 text-amber-500 fill-amber-400/20" />
                        </div>
                        <p className="text-sm font-semibold text-slate-880 truncate w-full px-1" title={folder.name}>
                          {folder.name}
                        </p>
                        <p className="text-[11px] font-medium text-slate-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Open Folder →
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Files Main Layout */}
            {filteredFiles.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2 px-1">
                  <FiFile className="w-3.5 h-3.5" />
                  Files ({filteredFiles.length})
                </h3>
                
                {viewMode === 'grid' ? (
                  /* Grid Blueprint View */
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredFiles.map((file) => {
                      const FileIcon = getFileIcon(file.filename);
                      const isSelected = selectedFiles.includes(file.key);
                      const visualClasses = getFileColor(file.filename);

                      return (
                        <motion.div
                          key={file.key}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`bg-white rounded-2xl border transition-all relative flex flex-col justify-between p-4 group ${
                            isSelected 
                              ? 'border-indigo-500 ring-4 ring-indigo-500/5 shadow-md shadow-indigo-50/50' 
                              : 'border-slate-200/90 hover:border-slate-300 hover:shadow-sm'
                          }`}
                        >
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-3">
                                <label className="relative flex items-center cursor-pointer p-0.5">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleFileSelection(file.key)}
                                    className="peer sr-only"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className="w-4.5 h-4.5 bg-slate-50 border border-slate-300 rounded peer-checked:bg-indigo-600 peer-checked:border-indigo-600 peer-focus:ring-2 peer-focus:ring-indigo-500/20 transition-all flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                </label>
                                
                                <div className={`p-2 rounded-xl border ${visualClasses.split(' ')[1]} ${visualClasses.split(' ')[2]}`}>
                                  <FileIcon className={`w-5 h-5 ${visualClasses.split(' ')[0]}`} />
                                </div>
                              </div>
                              
                              <div className="flex items-center bg-slate-50 border border-slate-100 rounded-lg p-0.5 opacity-80 sm:opacity-0 group-hover:opacity-100 transition-opacity shadow-inner">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFilePreview(file);
                                  }}
                                  className="text-slate-400 hover:text-indigo-600 hover:bg-white p-1.5 rounded-md transition-all"
                                  title="Quick Inspection"
                                >
                                  <FiEye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFileToDelete(file.key);
                                    setShowDeleteModal(true);
                                  }}
                                  className="text-slate-400 hover:text-rose-600 hover:bg-white p-1.5 rounded-md transition-all"
                                  title="Delete Item"
                                >
                                  <FiTrash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            
                            <div className="mt-4">
                              <p className="text-sm font-semibold text-slate-800 truncate" title={file.filename}>
                                {file.filename}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[11px] font-mono font-medium text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                  {formatFileSize(file.size)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                            <span>
                              {file.lastModified ? new Date(file.lastModified).toLocaleDateString() : 'N/A'}
                            </span>
                            <a
                              href={`${API_BASE}${file.downloadUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-semibold text-indigo-600 hover:text-indigo-800 transition-colors bg-indigo-50/50 hover:bg-indigo-50 px-2 py-1 rounded-md"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FiDownload className="w-3 h-3" strokeWidth="2.5" />
                              Get File
                            </a>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  /* List Blueprint View */
                  <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50/70 border-b border-slate-200/80 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            <th className="p-3.5 w-10 text-center">
                              <span className="sr-only">Select</span>
                            </th>
                            <th className="p-3.5">Name</th>
                            <th className="p-3.5 w-32">File Size</th>
                            <th className="p-3.5 w-44">Last Modified</th>
                            <th className="p-3.5 w-36 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                          {filteredFiles.map((file) => {
                            const FileIcon = getFileIcon(file.filename);
                            const isSelected = selectedFiles.includes(file.key);
                            const visualClasses = getFileColor(file.filename);

                            return (
                              <tr 
                                key={file.key}
                                className={`hover:bg-slate-50/60 group transition-colors ${
                                  isSelected ? 'bg-indigo-50/30 hover:bg-indigo-50/40' : ''
                                }`}
                              >
                                <td className="p-3.5 text-center">
                                  <label className="relative flex items-center justify-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleFileSelection(file.key)}
                                      className="peer sr-only"
                                    />
                                    <div className="w-4 h-4 bg-slate-50 border border-slate-300 rounded peer-checked:bg-indigo-600 peer-checked:border-indigo-600 transition-all flex items-center justify-center">
                                      <svg className="w-2.5 h-2.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  </label>
                                </td>
                                <td className="p-3.5 font-medium text-slate-900 max-w-xs md:max-w-md truncate">
                                  <div className="flex items-center gap-3">
                                    <div className={`p-1.5 rounded-lg border flex-shrink-0 ${visualClasses.split(' ')[1]} ${visualClasses.split(' ')[2]}`}>
                                      <FileIcon className={`w-4 h-4 ${visualClasses.split(' ')[0]}`} />
                                    </div>
                                    <span className="truncate" title={file.filename}>{file.filename}</span>
                                  </div>
                                </td>
                                <td className="p-3.5 font-mono text-xs text-slate-600">
                                  {formatFileSize(file.size)}
                                </td>
                                <td className="p-3.5 text-xs text-slate-400 whitespace-nowrap">
                                  {file.lastModified 
                                    ? `${new Date(file.lastModified).toLocaleDateString()} ${new Date(file.lastModified).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` 
                                    : 'N/A'
                                  }
                                </td>
                                <td className="p-3.5 text-right">
                                  <div className="inline-flex items-center gap-1 bg-slate-50 border border-slate-100 rounded-lg p-0.5 shadow-inner opacity-80 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => handleFilePreview(file)}
                                      className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-md transition-all"
                                      title="Inspect File"
                                    >
                                      <FiEye className="w-3.5 h-3.5" />
                                    </button>
                                    <a
                                      href={`${API_BASE}${file.downloadUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-md transition-all"
                                      title="Download File"
                                    >
                                      <FiDownload className="w-3.5 h-3.5" />
                                    </a>
                                    <button
                                      onClick={() => {
                                        setFileToDelete(file.key);
                                        setShowDeleteModal(true);
                                      }}
                                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-white rounded-md transition-all"
                                      title="Delete Item"
                                    >
                                      <FiTrash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty States */}
            {filteredFolders.length === 0 && filteredFiles.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                {searchTerm ? (
                  <div className="max-w-xs mx-auto">
                    <div className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                      <FiSearch className="w-5 h-5 text-slate-400" />
                    </div>
                    <p className="text-slate-700 font-semibold text-sm">No match matches found</p>
                    <p className="text-slate-400 text-xs mt-1">We couldn't find anything matching "{searchTerm}" inside this folder.</p>
                  </div>
                ) : (
                  <div className="max-w-xs mx-auto">
                    <div className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                      <FiFolder className="w-5 h-5 text-slate-300" />
                    </div>
                    <p className="text-slate-700 font-semibold text-sm">Empty Folder Space</p>
                    <p className="text-slate-400 text-xs mt-1">
                      {currentPath ? `There are no file objects or directories located inside "${currentPath}".` : 'Your root drive space is currently completely empty.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Single Item Discard Dialog Modal */}
        <AnimatePresence>
          {showDeleteModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 shadow-xl"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 10 }}
                className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3.5 text-rose-600 mb-3">
                  <div className="p-2.5 bg-rose-50 border border-rose-100 rounded-xl">
                    <FiAlertCircle className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Permanently discard file?</h3>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed mb-6">
                  Are you absolutely sure you want to delete this specific file? Once wiped out, you will not be able to restore this data.
                </p>
                <div className="flex justify-end gap-2.5">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-xl transition-colors"
                  >
                    Keep File
                  </button>
                  <button
                    onClick={() => handleDeleteFile(fileToDelete)}
                    disabled={deleting}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-rose-100 active:scale-95 disabled:opacity-50"
                  >
                    {deleting ? 'Wiping...' : 'Yes, Delete'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Custom Empty All Folder Warning Modal */}
        <AnimatePresence>
          {showDeleteAllModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 shadow-xl"
              onClick={() => setShowDeleteAllModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 10 }}
                className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3.5 text-rose-600 mb-3">
                  <div className="p-2.5 bg-rose-50 border border-rose-100 rounded-xl">
                    <FiAlertCircle className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Purge entire folder?</h3>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed mb-6">
                  This action drops **ALL** files present in this current space directory layout. None of these items can be recovered.
                </p>
                <div className="flex justify-end gap-2.5">
                  <button
                    onClick={() => setShowDeleteAllModal(false)}
                    className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-xl transition-colors"
                  >
                    Cancel Action
                  </button>
                  <button
                    onClick={handleDeleteAll}
                    disabled={deleting}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-rose-100 active:scale-95 disabled:opacity-50"
                  >
                    {deleting ? 'Purging Hub...' : 'Purge Everything'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dynamic Media File Inspection & Preview Lightbox */}
        <AnimatePresence>
          {showPreviewModal && selectedFilePreview && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6"
              onClick={() => setShowPreviewModal(false)}
            >
              <motion.div
                initial={{ scale: 0.98, y: 15 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.98, y: 15 }}
                className="bg-white rounded-2xl max-w-5xl w-full max-h-[88vh] overflow-hidden shadow-2xl border border-slate-100 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-xl border bg-white ${getFileColor(selectedFilePreview.filename).split(' ')[2]}`}>
                      {getFileIcon(selectedFilePreview.filename)({
                        className: `w-4 h-4 ${getFileColor(selectedFilePreview.filename).split(' ')[0]}`
                      })}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate max-w-xs md:max-w-lg" title={selectedFilePreview.filename}>
                        {selectedFilePreview.filename}
                      </p>
                      <p className="text-[11px] font-mono text-slate-400 mt-0.5">{formatFileSize(selectedFilePreview.size)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <a
                      href={`${API_BASE}${selectedFilePreview.downloadUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-white border border-transparent hover:border-slate-200/60 rounded-xl transition-all"
                      title="Download Asset Link"
                    >
                      <FiDownload className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => setShowPreviewModal(false)}
                      className="p-2 text-slate-500 hover:text-slate-800 hover:bg-white border border-transparent hover:border-slate-200/60 rounded-xl transition-all"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Modal Body Viewport */}
                <div className="p-5 overflow-y-auto bg-white flex-1 min-h-0">
                  {renderPreviewContent()}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </TenantAdminLayout>
  );
};

export default StorageManagement;